'use client'

import { AddProductToFavoriteButton } from '@/feature/product/components/add-product-to-favorite-button/AddProductToFavoriteButton.client'
import { BobButton } from '@/feature/product/components/product-bob/BobButton.client'
import { ProductQrCodeDrawer } from '@/feature/product/components/product-qr-code-drawer/ProductQrCodeDrawer.client'
import { useProductsListFavoriteStatus } from '@/feature/product/hooks/useProductsListFavoriteStatus'
import { EmbedShareButton } from '@/feature/share/components/EmbedShareButton'
import { isSSR } from '@/lib/next/services/nextjs.rendering'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { PrintButton } from '@/ui/buttons/print-button/PrintButton.client'
import { ProductType } from '@gql-generated/graphql'
import { useParams } from 'next/navigation'
import { useCallback } from 'react'
import { ShareButton } from '../product-share/ShareButton.client'

type ActionButtonsProps = {
  isFavorite?: boolean
  /** The product reference */
  productReference: string

  /** The product name */
  productName: string

  productType: ProductType
}

export function ActionButtons({
  isFavorite,
  productName,
  productType,
  productReference,
}: ActionButtonsProps): React.ReactElement {
  const { data: favoriteMapData } = useProductsListFavoriteStatus(
    productType === ProductType.Article ? [productReference] : [],
  )
  const product = {
    isFavorite,
    name: productName,
    reference: productReference,
    type: productType,
  }
  const { slug } = useParams()
  const url = !isSSR ? new URL(`${window.location.origin}/${slug}`).toString() : ''

  const handleProductQrCodeDrawerOpen = useCallback(() => {
    pushToGtmDataLayer({
      event: 'gaEvent',
      eventCategory: 'étiquettes',
      eventAction: 'page article, produit',
      eventLabel: 'bloc présentation article, cta étiquettes',
    })
  }, [])

  const handleProductsStickersDrawerOpen = useCallback(() => {
    pushToGtmDataLayer({
      event: 'gaEvent',
      eventCategory: 'étiquettes',
      eventAction: 'page article, produit',
      eventLabel: 'drawer, cta imprimer étiquette',
    })
  }, [])

  const handleProductsStickersDownloadSuccess = useCallback((count: number) => {
    pushToGtmDataLayer({
      event: 'gaEvent',
      eventCategory: 'étiquettes',
      eventAction: 'page article, produit',
      eventLabel: `cta télécharger | ${count} étiquettes`,
    })
  }, [])

  return (
    <div className="flex gap-1">
      {product.type === ProductType.Article && (
        <ProductQrCodeDrawer
          productReference={productReference}
          productName={productName}
          onProductQrCodeDrawerOpen={handleProductQrCodeDrawerOpen}
          onProductsStickersDownloadSuccess={handleProductsStickersDownloadSuccess}
          onProductsStickersDrawerOpen={handleProductsStickersDrawerOpen}
        />
      )}
      <ShareButton productName={productName} />
      <EmbedShareButton title={productName} url={url} />
      <PrintButton />
      {product.type === ProductType.Article && (
        <AddProductToFavoriteButton
          product={{
            reference: productReference,
            name: productName,
            isFavorite: favoriteMapData?.[productReference],
          }}
          context="product-page"
        />
      )}
      <BobButton />
    </div>
  )
}
